import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";
import { Box, IconButton, Tab, Tabs, Typography } from "@mui/material";

import APP_ROUTES from "../../../../../routers/routeConstants";
import useIsOwn from "../../../../../hooks/useIsOwn";

import { Service } from "../../../types/service.type";
import { ServiceDetailsBackIcon } from "../../../assets/icons/service-details-icons";
import ServiceApprovalStatus from "../../../components/ServiceApprovalStatus";

const ProgramRequestHeader = ({ service, isServiceLoading }: { service?: Service; isServiceLoading?: boolean }) => {
  const { t, i18n } = useTranslation("goService", { keyPrefix: "programRequestPage" });
  const navigate = useNavigate();

  const { isOwn: isOwnService, isLoading: isOwnServiceLoading } = useIsOwn({
    userId: service?.user?.id,
    isUserDataLoading: isServiceLoading ?? false,
  });

  const handleBack = () => navigate(APP_ROUTES.MY_SERVICES);

  return (
    <Box sx={styles.container}>
      <Typography sx={styles.title}>
        <IconButton size="small" onClick={handleBack}>
          <ServiceDetailsBackIcon />
        </IconButton>
        {service?.name ?? "--"}
        {isOwnService && <ServiceApprovalStatus service={service} isLoading={isOwnServiceLoading} />}
      </Typography>
      <ProgramRequestTabsNavigation />
    </Box>
  );
};

const TABS = ["about", "videos", "reviews"] as const;
type TabType = (typeof TABS)[number];

const ProgramRequestTabsNavigation = () => {
  const { t } = useTranslation("goService", { keyPrefix: "programRequestPage" });

  const navigate = useNavigate();
  const location = useLocation();

  const searchParams = new URLSearchParams(location.search);
  const currentTab = searchParams.get("tab") || "about";

  const handleTabChange = (event: React.SyntheticEvent, newValue: TabType) => {
    const searchParams = new URLSearchParams(location.search);
    searchParams.set("tab", newValue);

    navigate({ search: searchParams.toString() });
  };

  return (
    <Tabs
      value={currentTab}
      onChange={handleTabChange}
      aria-label="program request tabs"
      sx={{
        minHeight: "fit-content",
        "& .MuiTabs-flexContainer": {
          gap: "24px",
        },
      }}
    >
      {TABS.map((tab) => {
        return <Tab key={tab} label={t(tab)} value={tab} sx={styles.tabStyle(tab === currentTab)} />;
      })}
    </Tabs>
  );
};

export default ProgramRequestHeader;

const styles = {
  container: {
    flex: 1,
    display: "flex",
    flexDirection: "column",
    gap: "24px",
    borderRadius: "8px",
    border: "1px solid #EAECF0",
    background: "var(--White, #FFF)",
    padding: "20px 20px 0",
  },
  title: {
    color: "var(--black-100, #1C1C1C)",
    fontFamily: "Montserrat",
    fontSize: "20px",
    fontStyle: "normal",
    fontWeight: 800,
    lineHeight: "normal",
    display: "flex",
    alignItems: "center",
    gap: "8px",
  },
  tabStyle: (isActive: boolean) => ({
    color: isActive ? "var(--Brand-700, #076D0D)" : "var(--Gray-500, #667085)",
    fontFamily: "Montserrat",
    fontSize: "14px",
    fontStyle: "normal",
    fontWeight: 600,
    lineHeight: "20px",
    padding: "0px 4px 12px 4px",
    textTransform: "none",
    minHeight: "fit-content",
    minWidth: "fit-content",
  }),
};
